import type { MeResponse } from '@agent-platform/shared';
import { useMutation } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { Building2, Check, ChevronsUpDown } from 'lucide-react';
import { toast } from 'sonner';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { api, errorMessage } from '@/lib/api';
import { ROLE_LABELS } from '@/lib/format';
import { useApplyMe, useMe } from '@/lib/queries';

export function useSwitchTenant() {
  const applyMe = useApplyMe();
  const navigate = useNavigate();
  return useMutation({
    mutationFn: (tenantId: string) =>
      api<MeResponse>('/session/tenant', { method: 'POST', body: { tenantId } }),
    onSuccess: (me) => {
      applyMe(me);
      void navigate({ to: '/' });
      toast.success(`Switched to ${me.activeTenant?.name ?? 'tenant'}`);
    },
    onError: (error) => toast.error(errorMessage(error)),
  });
}

export function TenantSwitcher() {
  const me = useMe();
  const switchTenant = useSwitchTenant();
  const active = me.activeTenant;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          className="bg-sidebar-accent/70 hover:bg-sidebar-accent text-sidebar-accent-foreground h-8 w-full justify-between gap-2 rounded-[10px] px-2.5 text-sm font-medium"
          aria-label="Switch tenant"
        >
          <Building2 className="text-sidebar-foreground size-4 shrink-0" />
          <span className="flex-1 truncate text-left">{active?.name ?? 'No tenant'}</span>
          {active?.status === 'suspended' && <Badge variant="destructive">Suspended</Badge>}
          <ChevronsUpDown className="size-4 shrink-0 opacity-50" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel>Tenants</DropdownMenuLabel>
        <DropdownMenuSeparator />
        {me.memberships.length === 0 && (
          <DropdownMenuItem disabled>You are not a member of any tenant</DropdownMenuItem>
        )}
        {me.memberships.map((m) => (
          <DropdownMenuItem
            key={m.tenantId}
            disabled={switchTenant.isPending}
            onSelect={() => m.tenantId !== active?.id && switchTenant.mutate(m.tenantId)}
          >
            <span className="flex-1 truncate">{m.tenantName}</span>
            <span className="text-muted-foreground text-xs">{ROLE_LABELS[m.role]}</span>
            {m.tenantId === active?.id && <Check className="size-4" />}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
